import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import { useTelemetry } from "../shared/useTelemetry";
import { normalizeAssignmentStatus } from "./assignmentStatus";
import type { MaterialOption } from "./materialOption";
import type {
    AssignmentSummary,
    AssignmentCreateRequest,
    AssignmentUpdateRequest,
    AssignmentResponse,
    AssignmentArea,
    AddressCountRequest,
    AddressCountResponse
} from "./types";

const normalizeAssignmentResponse = (item: AssignmentResponse): AssignmentResponse => ({
    ...item,
    status: normalizeAssignmentStatus(item.status)
});

const fallbackAssignments: AssignmentSummary[] = [
    {
        id: "sample-1",
        name: "Stockholm Innerstad",
        ownerName: "Anna Svensson",
        remainingAddresses: 132,
        progress: 0.68
    },
    {
        id: "sample-2",
        name: "Uppsala Centrum",
        ownerName: "Björn Karlsson",
        remainingAddresses: 58,
        progress: 0.84
    }
];

export const fetchAssignments = async (): Promise<AssignmentSummary[]> => {
    try {
        const { data } = await apiClient.get<AssignmentSummary[]>(
            "/assignments/summary"
        );
        return data;
    } catch {
        return fallbackAssignments;
    }
};

export const getAssignments = async (
    campaignId?: string,
    distributorId?: string,
    municipalityId?: string
): Promise<AssignmentResponse[]> => {
    const params = new URLSearchParams();
    if (campaignId) params.append("campaignId", campaignId);
    if (distributorId) params.append("distributorId", distributorId);
    if (municipalityId) params.append("municipalityId", municipalityId);

    const queryString = params.toString();
    const url = `/assignments${queryString ? `?${queryString}` : ""}`;

    const { data } = await apiClient.get<{ items: AssignmentResponse[] }>(url);
    return data.items.map(normalizeAssignmentResponse);
};

/**
 * Gets active assignments for the current user
 */
export const getActiveAssignments = async (): Promise<AssignmentResponse[]> => {
    const { data } = await apiClient.get<{ items: AssignmentResponse[] }>("/assignments/active");
    return data.items.map(normalizeAssignmentResponse);
};

export const getAssignment = async (
    id: string
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.get<AssignmentResponse>(`/assignments/${id}`);
    return normalizeAssignmentResponse(data);
};

export const createAssignment = async (
    request: AssignmentCreateRequest
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.post<AssignmentResponse>(
        "/assignments",
        request
    );
    return normalizeAssignmentResponse(data);
};

export const updateAssignment = async (
    id: string,
    request: AssignmentUpdateRequest
): Promise<AssignmentResponse> => {
    const { data } = await apiClient.put<AssignmentResponse>(
        `/assignments/${id}`,
        request
    );
    return normalizeAssignmentResponse(data);
};

export const deleteAssignment = async (id: string): Promise<void> => {
    await apiClient.delete(`/assignments/${id}`);
};

/**
 * React Query mutation hook for creating a new assignment
 */
export const useCreateAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry(); return useMutation({
        mutationFn: createAssignment,
        onSuccess: () => {
            telemetry.track("assignment.created");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.failed", error);
        }
    });
};

/**
 * React Query mutation hook for updating an existing assignment
 */
export const useUpdateAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: ({ id, request }: { id: string; request: AssignmentUpdateRequest }) =>
            updateAssignment(id, request),
        onSuccess: () => {
            telemetry.track("assignment.updated");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.updateFailed", error);
        }
    });
};

/**
 * React Query mutation hook for deleting an assignment
 */
export const useDeleteAssignment = () => {
    const queryClient = useQueryClient();
    const telemetry = useTelemetry();

    return useMutation({
        mutationFn: deleteAssignment,
        onSuccess: () => {
            telemetry.track("assignment.deleted");
            // Invalidate map queries to refresh assignment polygons
            queryClient.invalidateQueries({ queryKey: ["map"] });
            queryClient.invalidateQueries({ queryKey: ["assignments"] });
        },
        onError: (error: unknown) => {
            telemetry.trackError("assignment.deleteFailed", error);
        }
    });
};

/** Uppdrag så som de visas på den öppna utdelarlänken – utan adress och telefon. */
export interface SharedAssignment {
    id: string;
    name: string;
    kretsMemberId?: string;
    distributorName?: string;
    targetAddressCount?: number;
    /** Följer med så att utdelaren kan ladda ned kartan för ett område. */
    area?: AssignmentArea;
}

/** En utdelares ja/nej för en enskild omgång, plus om materialet är utlämnat. */
export interface SharedRoundAnswer {
    roundId: string;
    /** null/undefined = inget svar ännu för den omgången. */
    canDeliver?: boolean | null;
    materialReceived: boolean;
}

export interface SharedDistributor {
    id: string;
    name: string;
    /** Ett svar per öppen omgång; saknad post = inget svar för den omgången. */
    answers: SharedRoundAnswer[];
    /** Hur utdelaren vill få materialet – samma val för alla omgångar. */
    materialOption?: MaterialOption | null;
}

export interface SharedRound {
    id: string;
    name: string;
    startedAt: string;
}

export interface SharedAssignmentList {
    municipalityPolygonId: string;
    municipalityName: string;
    /** Öppna omgångar, äldst först. Tom lista = ingen utdelning att svara på ännu. */
    rounds: SharedRound[];
    distributors: SharedDistributor[];
    items: SharedAssignment[];
}

export interface SharedResponse {
    kretsMemberId: string;
    answers: SharedRoundAnswer[];
    materialOption?: MaterialOption | null;
    respondedAt?: string;
}

export interface AssignmentShareLink {
    municipalityPolygonId: string;
    municipalityName: string;
    token: string;
    createdAt: string;
}

/** Hämtar (eller skapar) kommunens delningstoken. Admin. */
export const getAssignmentShareLink = async (
    municipalityId: string,
    regenerate = false
): Promise<AssignmentShareLink> => {
    const { data } = await apiClient.get<AssignmentShareLink>("/assignments/share-link", {
        params: { municipalityId, regenerate }
    });
    return data;
};

/** Utdelarlänkens lista – anonym, token är enda nyckeln. */
export const getSharedAssignments = async (token: string): Promise<SharedAssignmentList> => {
    const { data } = await apiClient.get<SharedAssignmentList>("/assignments/shared", {
        params: { token }
    });
    return data;
};

/** Utdelarens eget svar för de öppna omgångarna – aldrig någon annans. */
export const getSharedResponse = async (
    token: string,
    kretsMemberId: string
): Promise<SharedResponse> => {
    const { data } = await apiClient.get<SharedResponse>("/assignments/shared/response", {
        params: { token, kretsMemberId }
    });
    return data;
};

export const setSharedResponse = async (request: {
    token: string;
    kretsMemberId: string;
    /** Omgångarna utdelaren kan vara med på; övriga öppna omgångar sparas som nej. */
    roundIds: string[];
    materialOption: MaterialOption | null;
}): Promise<SharedResponse> => {
    const { data } = await apiClient.put<SharedResponse>("/assignments/shared/response", {
        token: request.token,
        kretsMemberId: request.kretsMemberId,
        roundIds: request.roundIds,
        materialOption: request.materialOption ?? undefined
    });
    return data;
};

/**
 * Lägger till en utdelare som saknas i listan. Anonymt – token är enda nyckeln, precis som för
 * svaren. Adressen krävs: utan den går det inte att rita ett utdelningsområde.
 */
export const registerSharedDistributor = async (request: {
    token: string;
    name: string;
    homeAddress: string;
    homeLatitude: number | null;
    homeLongitude: number | null;
    phoneNumber: string;
}): Promise<SharedDistributor> => {
    const { data } = await apiClient.post<SharedDistributor>("/assignments/shared/distributor", {
        token: request.token,
        name: request.name,
        homeAddress: request.homeAddress,
        homeLatitude: request.homeLatitude ?? undefined,
        homeLongitude: request.homeLongitude ?? undefined,
        phoneNumber: request.phoneNumber || undefined
    });
    return data;
};

/**
 * Counts addresses within a polygon area
 */
export const countAddressesInArea = async (
    request: AddressCountRequest
): Promise<AddressCountResponse> => {
    try {
        const response = await apiClient.post<AddressCountResponse>(
            "/assignments/count-addresses",
            request,
            {
                timeout: 60000 // 60 second timeout for spatial queries
            }
        );
        return response.data;
    } catch (error: any) {
        console.error("[API] Failed to count addresses:", error);
        throw error;
    }
};